import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import animationStyles from 'src/styles/AddToCartAnimation.module.css';
import { CART_TYPE } from '@/constants/cart';
import { ASSISTED_USER_ID, ASSISTED_USER_NAME, ASSISTED_USER_NUMBER, NAVIGATION_FROM_IS_ASSISTED_ORDERING } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import useFetchCartProductVariants from '@/hooks/useFetchCartProductVariants';
import useIsMobile from '@/hooks/useIsMobile';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { selectCurrentVendor } from '@/store/features/locationSlice';
import { conversionLabels } from '@/utils/analytics/conversionLabels';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import {
// trackCustomEvent,
trackGAConversionEvent } from '@/utils/analytics/googleAnalytics';
import { isShareChatUtmSession } from '@/utils/analytics/utmParams';
import { getNumberOfItemsInCart, getTotalComboCost, getTotalCost } from '@/utils/cart';
import { getCookieValue } from '@/utils/helper';
import Paragraph, { VARIANT } from './Paragraph';
interface Props {
  className?: string;
}
const CheckoutPopup = ({
  className = 'bottom-0'
}: Props) => {
  const {
    t
  } = useTranslation('common');
  const {
    cartItems,
    comboItems,
    cartType
  } = useContext(CartContext);
  const {
    variants
  } = useFetchCartProductVariants();
  const totalVariantCost = getTotalCost(variants.product, cartItems) + getTotalComboCost(variants.combo, comboItems);
  const currentVendor = useSelector(selectCurrentVendor);
  const noOfItemsInCart = getNumberOfItemsInCart({
    ...cartItems,
    ...comboItems
  });
  const {
    pathname
  } = useRouter();
  const {
    isMobile,
    isWindowLoading
  } = useIsMobile();
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const orderingFor = getCookieValue(NAVIGATION_FROM_IS_ASSISTED_ORDERING) === 'true';
  const isAssumeUser = [getCookieValue(ASSISTED_USER_ID), getCookieValue(ASSISTED_USER_NAME), getCookieValue(ASSISTED_USER_NUMBER)].every(Boolean);
  const onBuyNowClick = () => {
    if (typeof window !== 'undefined' && window.scq && isShareChatUtmSession()) {
      window.scq('Initiate checkout', 'pre_defined');
    }
    const eventParam = {
      CartValue: totalVariantCost,
      vendorId: currentVendor?.id,
      vendorName: currentVendor?.company_name
    };
    trackCustomEvent(CustomEventTypes.BUY_NOW, JSON.stringify(eventParam));
    trackGAConversionEvent(CustomEventTypes.BUY_NOW, {}, conversionLabels.BUY_NOW);
  };
  if (!isMobile && !isWindowLoading && !pathname?.includes('/ondc')) {
    return null;
  }
  const checkoutHref = orderingFor && !isAssumeUser ? '/select-farmer' : `${cartType === CART_TYPE.ONDC ? '/ondc' : ''}/cart`;
  return <>
      {currentVendor?.id && noOfItemsInCart > 0 ?
    // Whole bar is the tap target (not just the button) so a tap anywhere
    // navigates. The navigation fires routeChangeStart, which shows the
    // full-screen NavigationLoader for immediate feedback.
    <Link data-testid="checkout-popup" href={checkoutHref} onClick={onBuyNowClick} className={`fixed left-0 w-full h-[60px] bg-primary-80 z-30 text-white px-4 py-2 block ${animationStyles.cartBar} ${className}`}>
          <div className="flex justify-between items-center m-auto max-w-7xl h-full">
            <Paragraph variant={VARIANT.BIG}>{`${noOfItemsInCart} ${t(noOfItemsInCart > 1 ? 'items' : 'item')}`}</Paragraph>
            <span data-testid="buy-now-link" className="flex justify-center items-center bg-white rounded-md w-36 h-full text-primary-80">
              {t('buy_now')}
            </span>
          </div>
        </Link> : null}
    </>;
};
export default CheckoutPopup;